import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsPagePath, roomDashboardPath } from '../utils/alertNavigation';
import { safeGetItem, safeSetItem } from '../utils/safeStorage';
import { apiClient } from '../services/api';
import logger from '../utils/logger';

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  type: string;
  severity: string;
  status: string;
  alert_id: number | null;
  room_id: number | null;
  created_at: string;
  read_at: string | null;
}

// Check if sound is enabled (stored in localStorage)
const isSoundEnabled = (): boolean => {
  const setting = safeGetItem('notification_sound_enabled');
  return setting !== 'false'; // Default to true
};

// Toggle sound setting
export const toggleNotificationSound = (enabled: boolean) => {
  safeSetItem('notification_sound_enabled', enabled.toString());
};

// Get current sound setting
export const getNotificationSoundEnabled = (): boolean => {
  return isSoundEnabled();
};

// Create notification sound using Web Audio API
const createNotificationSound = (frequency: number = 800, duration: number = 0.2): void => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  } catch (e) {
    logger.warn('Could not play notification sound:', e);
  }
};

// Play notification sound
export const playNotificationSound = (severity: string = 'info') => {
  if (!isSoundEnabled()) {
    logger.log('Sound disabled, skipping');
    return;
  }

  logger.log('Playing notification sound for severity:', severity);

  try {
    // Different tones for different severities
    if (severity === 'critical') {
      // Urgent: Two high-pitched beeps
      createNotificationSound(1000, 0.15);
      setTimeout(() => createNotificationSound(1000, 0.15), 200);
      setTimeout(() => createNotificationSound(1200, 0.2), 450);
    } else if (severity === 'warning') {
      // Warning: Medium tone
      createNotificationSound(700, 0.2);
      setTimeout(() => createNotificationSound(800, 0.15), 250);
    } else {
      // Info: Gentle chime
      createNotificationSound(600, 0.15);
      setTimeout(() => createNotificationSound(800, 0.2), 150);
    }
  } catch (e) {
    logger.warn('Could not play sound:', e);
  }
};

const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastCount, setLastCount] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const token = safeGetItem('access_token');
      if (!token) return;

      const response = await apiClient.get('/notifications/unread-count');
      const data = response.data;

      // Play sound if count increased (new notification arrived)
      if (data.count > lastCount && lastCount >= 0) {
        playNotificationSound('info');
      }
      setLastCount(data.count);
      setUnreadCount(data.count);
    } catch (error) {
      // Silently fail for polling requests
    }
  }, [lastCount]);

  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000); // Every 10 seconds
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/notifications', { params: { limit: 20 } });
      setNotifications(response.data);
    } catch (error) {
      // Silently fail
    } finally {
      setLoading(false);
    }
  };

  const handleBellClick = () => {
    if (!isOpen) {
      fetchNotifications();
    }
    setIsOpen(!isOpen);
  };

  const markAsRead = async (id: number) => {
    try {
      await apiClient.put(`/notifications/${id}/read`);
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, status: 'read' } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // Silently fail
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
      setUnreadCount(0);
    } catch (error) {
      // Silently fail
    }
  };

  const dismissNotification = async (id: number) => {
    try {
      await apiClient.delete(`/notifications/${id}`);
      const notification = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notification?.status === 'unread') {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      // Silently fail
    }
  };

  const clearAllNotifications = async () => {
    try {
      await apiClient.delete('/notifications');
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      // Silently fail
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'border-l-red-500 bg-red-50';
      case 'warning': return 'border-l-yellow-500 bg-yellow-50';
      default: return 'border-l-blue-500 bg-blue-50';
    }
  };

  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button */}
      <button
        onClick={handleBellClick}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
        aria-label="Notifications"
      >
        {/* Bell SVG */}
        <svg
          className={`w-6 h-6 ${unreadCount > 0 ? 'text-teal-600' : 'text-gray-600'}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-2xl border border-gray-200 z-50 max-h-[80vh] overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-teal-500 to-teal-600">
            <h3 className="font-semibold text-white flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Notifications
              {unreadCount > 0 && (
                <span className="bg-white text-teal-600 text-xs font-bold px-2 py-0.5 rounded-full">
                  {unreadCount}
                </span>
              )}
            </h3>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-teal-100 hover:text-white font-medium"
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={clearAllNotifications}
                  className="text-sm text-teal-100 hover:text-white font-medium"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Notification List */}
          <div className="overflow-y-auto max-h-96">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full mx-auto"></div>
                <p className="mt-2">Loading...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-16 h-16 mx-auto text-gray-200 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <p className="text-gray-400 font-medium">No notifications</p>
                <p className="text-gray-300 text-sm mt-1">You're all caught up!</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`px-4 py-3 border-b border-gray-100 border-l-4 ${getSeverityColor(notification.severity)} ${
                    notification.status === 'unread' ? '' : 'opacity-60'
                  } hover:bg-gray-50 cursor-pointer transition-all`}
                  title={notification.alert_id ? 'Open this alert' : notification.room_id ? 'Show this room' : undefined}
                  onClick={() => {
                    if (notification.status === 'unread') markAsRead(notification.id);
                    // Take the user to the thing the notification is about
                    const dest = notification.alert_id
                      ? alertsPagePath(notification.alert_id)
                      : notification.room_id ? roomDashboardPath(notification.room_id) : null;
                    if (dest) { setIsOpen(false); navigate(dest); }
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {notification.status === 'unread' && (
                          <span className="w-2 h-2 bg-teal-500 rounded-full flex-shrink-0"></span>
                        )}
                        <p className={`text-sm ${notification.status === 'unread' ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'} truncate`}>
                          {notification.title}
                        </p>
                      </div>
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {getTimeAgo(notification.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissNotification(notification.id);
                      }}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors flex-shrink-0"
                      title="Dismiss"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
